import React from 'react';
import { X, Calculator, DollarSign, Users, Building2, Percent, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

const B2BROIForm = ({ data, errors, onChange, onCalculate, onBack, onClose }) => {
  const { t } = useTranslation();

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="roi-calculator-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('roiCalculator.corporate.formTitle')}</h2>
                <p className="text-blue-100 text-sm">{t('roiCalculator.corporate.formSubtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-5">
            <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              ← {t('roiCalculator.selectType').split(':')[0]}
            </button>
            
            {/* Total Employees */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                {t('roiCalculator.corporate.totalEmployees')} *
              </label>
              <input
                type="text"
                value={formatNumber(data.totalEmployees)}
                onChange={(e) => onChange('totalEmployees', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.totalEmployees ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="500"
              />
              {errors.totalEmployees && <p className="text-red-500 text-xs mt-1">{errors.totalEmployees}</p>}
            </div>

            {/* Number of Participants */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 text-blue-600" />
                {t('roiCalculator.corporate.participants')} *
              </label>
              <input
                type="text"
                value={formatNumber(data.participants)}
                onChange={(e) => onChange('participants', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.participants ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="50"
              />
              {errors.participants && <p className="text-red-500 text-xs mt-1">{errors.participants}</p>}
            </div>

            {/* Average Salary */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 text-blue-600" />
                {t('roiCalculator.corporate.avgSalary')} *
              </label>
              <input
                type="text"
                value={formatNumber(data.avgSalary)}
                onChange={(e) => onChange('avgSalary', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.avgSalary ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="750,000"
              />
              {errors.avgSalary && <p className="text-red-500 text-xs mt-1">{errors.avgSalary}</p>}
            </div>

            {/* Turnover Rate */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Percent className="w-4 h-4 text-blue-600" />
                {t('roiCalculator.corporate.turnoverRate')} *
              </label>
              <input
                type="text"
                value={data.turnoverRate}
                onChange={(e) => onChange('turnoverRate', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.turnoverRate ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="20"
              />
              {errors.turnoverRate && <p className="text-red-500 text-xs mt-1">{errors.turnoverRate}</p>}
            </div>

            {/* Annual Revenue */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                {t('roiCalculator.corporate.annualRevenue')} *
              </label>
              <input
                type="text"
                value={formatNumber(data.annualRevenue)}
                onChange={(e) => onChange('annualRevenue', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.annualRevenue ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="100,000,000"
              />
              {errors.annualRevenue && <p className="text-red-500 text-xs mt-1">{errors.annualRevenue}</p>}
            </div>

            <Button
              onClick={onCalculate}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 text-lg font-semibold rounded-lg flex items-center justify-center gap-2"
            >
              <Calculator className="w-5 h-5" />
              {t('roiCalculator.calculateBtn')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BROIForm;
